import { useCallback, useEffect, lazy, Suspense, useRef, startTransition, useState } from 'react'
import { QuickView } from '~/app/features/QuickView'
import { useDispatch, useSelector } from 'react-redux'
import { getQuickViewId } from '~/app/store/debug'
import { resetQuickViewId, setIsSimilarProduct } from '~/app/store/debug/actions'
import { getIsDirectQuickView, getSimilarProduct, getTakeOverQV } from '~/app/store/debug/reducer'
import { GalleryQuickViewSpinner } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/GalleryQuickViewSpinner'
import { resetSimilarTemplates } from '~/app/store/similarTemplates/action'
import { ThunkDispatch } from '~/app/store/interface'
import { useValidQuickViewEntity } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/hooks/useValidQuickViewEntity'
import { trackDirectQVDismissImpression } from '~/app/store/analytics/thunks'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { similarProductsReset } from '~/app/store/similarProducts/action'
import { useLogger } from '@vp/ubik-context'
import { LoggingErrorBoundary } from '~/app/features/Gallery/components/ErrorBoundary'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { FEATURE_GATES } from 'shared/featureGates/constants'
import { useNewQuickViewProps } from '~/app/features/Gallery/components/PreviewArea/hooks/useNewQuickViewProps'

const GalleryQuickView = lazy(() => import('~/app/features/Gallery/components/PreviewArea/GalleryQuickView'))
const NewQuickView = lazy(() => import('@vp/quick-view-modal').then((module) => ({ default: module.QuickView })))

export const QuickViewModal = () => {
  const logger = useLogger()
  const dispatch = useDispatch<ThunkDispatch>()
  const isTakeOverQV = useSelector(getTakeOverQV)
  const designId = useSelector(getQuickViewId)
  const isDirectQuickView = useSelector(getIsDirectQuickView)
  const isSimilarProduct = !!useSelector(getSimilarProduct)
  const analytics = useAnalytics()
  const showNewQuickViewModal = useFeatureGate(FEATURE_GATES.GALLERY_QUICK_VIEW_MODAL_ENABLED)

  const tileEntity = useValidQuickViewEntity(designId)
  const {
    selectedDesignConceptId,
    mpvId,
    productQuantity,
    locale,
    designPersonalization,
    selectedOptions,
    taxonomyFilters,
    designCreationTypes,
    previouslyOrderedDate,
  } = useNewQuickViewProps(tileEntity)

  const handleOnRequestDismiss = useCallback((): void => {
    if (isTakeOverQV) {
      // go back one step in the history stack
      window.history.back()
      return
    }

    // Defer actions dispatching to hide modal immediately
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        startTransition(() => {
          if (isDirectQuickView && designId) {
            dispatch(trackDirectQVDismissImpression(designId, analytics))
          }

          if (isSimilarProduct) {
            dispatch(setIsSimilarProduct(false))
          }

          dispatch(resetQuickViewId(designId))
          dispatch(resetSimilarTemplates())
          dispatch(similarProductsReset())
        })
      })
    })
  }, [isTakeOverQV, isDirectQuickView, dispatch, designId, analytics, isSimilarProduct])

  const modalRef = useRef<HTMLDialogElement>(null)
  const [shouldRenderContent, setShouldRenderContent] = useState(false)

  // Defer content rendering to show modal immediately
  useEffect(() => {
    startTransition(() => {
      setShouldRenderContent(!!tileEntity)
    })
  }, [tileEntity])

  useEffect(() => {
    modalRef.current?.scrollTo(0, 0)
  }, [designId])

  if (showNewQuickViewModal) {
    return selectedDesignConceptId ? (
      <NewQuickView
        open={!!tileEntity}
        onClose={handleOnRequestDismiss}
        designConceptId={selectedDesignConceptId}
        mpvId={mpvId}
        quantity={productQuantity}
        locale={locale}
        designPersonalization={designPersonalization}
        selectedOptions={selectedOptions}
        taxonomyFilters={taxonomyFilters}
        designCreationTypes={designCreationTypes}
        previouslyOrderedDate={previouslyOrderedDate}
      />
    ) : null
  }

  return (
    <QuickView
      onlyRenderWhenOpen
      designId={designId}
      id={`quickview-${designId}`}
      isOpen={!!tileEntity}
      ref={modalRef}
      onRequestDismiss={handleOnRequestDismiss}
    >
      <LoggingErrorBoundary logger={logger}>
        {shouldRenderContent && <Suspense fallback={<GalleryQuickViewSpinner />}>{tileEntity && <GalleryQuickView tileEntity={tileEntity} />}</Suspense>}
        {!shouldRenderContent && <GalleryQuickViewSpinner />}
      </LoggingErrorBoundary>
    </QuickView>
  )
}
